import { useCallback, useEffect, useRef, useState } from "react";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { fetchSupportTicketDetails } from "../apis/fetchSupportTicketDetails";
import type { SupportTicketDetail } from "../supportTicketTypes";
import { mapSupportTicketDetailFromApiResponse } from "../utils/mapSupportTicketApiData";

/**
 * Options for refreshing ticket detail without clearing the current view.
 */
export interface RefetchSupportTicketDetailsOptions {
  /** When true, keeps the current detail visible while refreshing. */
  silent?: boolean;
}

/**
 * Return value for {@link useSupportTicketDetails}.
 */
export interface UseSupportTicketDetailsReturn {
  /** Full detail for the currently selected ticket. */
  ticketDetail: SupportTicketDetail | null;
  /** Whether the selected ticket detail request is in flight. */
  loadingDetail: boolean;
  /** Error message from the selected ticket detail request, if any. */
  detailError: string | null;
  /** Reloads detail for the currently selected ticket. */
  refetchTicketDetail: (
    options?: RefetchSupportTicketDetailsOptions,
  ) => Promise<void>;
}

/**
 * Loads full ticket detail whenever the selected ticket id changes.
 *
 * @param selectedTicketId - Sidebar-selected ticket id.
 * @returns Detail payload, loading flag, error state, and refetch handler.
 */
export function useSupportTicketDetails(
  selectedTicketId: string,
): UseSupportTicketDetailsReturn {
  const [ticketDetail, setTicketDetail] = useState<SupportTicketDetail | null>(
    null,
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const currentTicketIdRef = useRef(selectedTicketId);
  currentTicketIdRef.current = selectedTicketId;

  /**
   * Returns true when a completed request no longer matches the selected ticket.
   *
   * @param ticketId - Ticket id the in-flight request was started for.
   * @returns Whether the response should be ignored.
   */
  const isStaleRequest = useCallback((ticketId: string) => {
    return ticketId !== currentTicketIdRef.current;
  }, []);

  const loadTicketDetail = useCallback(
    async (
      ticketId: string,
      options?: RefetchSupportTicketDetailsOptions,
    ) => {
      if (!ticketId) {
        setTicketDetail(null);
        setDetailError(null);
        setLoadingDetail(false);
        return;
      }

      if (!options?.silent) {
        setLoadingDetail(true);
        setTicketDetail(null);
      }
      setDetailError(null);

      const orgId = getOrgIdForFetchExternalWorklist();
      if (!orgId) {
        if (isStaleRequest(ticketId)) {
          return;
        }
        setDetailError("Organization id is unavailable.");
        if (!options?.silent) {
          setLoadingDetail(false);
        }
        return;
      }

      try {
        const response = await fetchSupportTicketDetails({
          org_id: orgId,
          ticket_id: ticketId,
        });
        if (isStaleRequest(ticketId)) {
          return;
        }
        setTicketDetail(mapSupportTicketDetailFromApiResponse(response));
      } catch (error) {
        if (isStaleRequest(ticketId)) {
          return;
        }
        if (!options?.silent) {
          setTicketDetail(null);
        }
        setDetailError(
          error instanceof Error
            ? error.message
            : "Failed to load support ticket details.",
        );
      } finally {
        if (!options?.silent && !isStaleRequest(ticketId)) {
          setLoadingDetail(false);
        }
      }
    },
    [isStaleRequest],
  );

  const refetchTicketDetail = useCallback(
    async (options?: RefetchSupportTicketDetailsOptions) => {
      if (!selectedTicketId) {
        return;
      }

      await loadTicketDetail(selectedTicketId, options);
    },
    [loadTicketDetail, selectedTicketId],
  );

  useEffect(() => {
    void loadTicketDetail(selectedTicketId);
  }, [loadTicketDetail, selectedTicketId]);

  return {
    ticketDetail,
    loadingDetail,
    detailError,
    refetchTicketDetail,
  };
}
